import {GetServerSidePropsContext} from 'next'

import {brands, defaultBrandCode, getBrandByCode} from '../../../brand/config'
import {Brand, BrandCode, SubBrandCode, SubBrandKey} from '../../../brand/types'
import {getSearchHost} from '../../../brand/utils'
import {getIsProductionAppEnv} from '../../../utils/environment'
import {logger} from '../../../utils/logging/logger'
import {getUrlParam} from '../../../utils/url'
import {isBrowser} from '../../utils/environment'
import {warnServerEnvironment} from '../../utils/logging/server-warning'
import {
  getBrandOverrideFromCookie,
  persistBrandOverrideOnCookie
} from '../business/persistData'
import {shouldAllowOverrides} from './overrides'

/**
 * Log an error message to the console
 *
 * This function is used to log messages to the console. Those messages will end up in DataDog.
 * Once DataDog infra is implemented, we can remove this function and use DataDog directly.
 * @param message - The message to log
 */
export const logMessage = (message: string): void => {
  logger.error(message)
}

/**
 * Find brand code by exact domainCode match
 * @param hostnamePart - The hostname part to match against domainCode
 * @param brands - The brands record to search in
 * @returns The matching brand code or null if not found
 */
export const findBrandByDomainCode = (
  hostnamePart: string,
  brands: Record<BrandCode, Brand>
): BrandCode | null => {
  const brandEntries = Object.entries(brands) as Array<[BrandCode, Brand]>
  const match = brandEntries.find(
    ([, brand]) => brand.domainCode === hostnamePart
  )
  return match ? match[0] : null
}

/**
 * Find brand code by substring match
 * @param hostnamePart - The hostname part to search for brand code substrings
 * @param brands - The brands record to search in
 * @returns The matching brand code or null if not found
 */
export const findBrandByCodeSubstring = (
  hostnamePart: string,
  brands: Record<BrandCode, Brand>
): BrandCode | null => {
  const brandCodes = Object.keys(brands) as BrandCode[]
  return brandCodes.find(code => hostnamePart.includes(code)) || null
}

/**
 * Apply brand resolution strategy to hostname parts
 * @param hostnameParts - Array of hostname parts to search through
 * @param brands - The brands record to search in
 * @returns The resolved brand code or null if not found
 */
const resolveBrandFromHostnameParts = (
  hostnameParts: string[],
  brands: Record<BrandCode, Brand>
): BrandCode | null => {
  // Strategy 1: Find first part that matches a domainCode exactly
  for (const part of hostnameParts) {
    const domainMatch = findBrandByDomainCode(part, brands)
    if (domainMatch) return domainMatch
  }

  // Strategy 2: Find first part that contains a brand code
  for (const part of hostnameParts) {
    const codeMatch = findBrandByCodeSubstring(part, brands)
    if (codeMatch) return codeMatch
  }

  return null
}

/**
 * Get the brand code from the hostname
 * @param hostname - The hostname to get the brand code for
 * @param brands - The brands to check against
 * @returns The brand code
 */
export const getBrandCodeByHost = (
  hostname: string,
  brands: Record<BrandCode, Brand>
): BrandCode => {
  const hostnameParts = hostname.split('.')
  const resolvedBrand = resolveBrandFromHostnameParts(hostnameParts, brands)

  if (resolvedBrand) return resolvedBrand

  if (!hostname.startsWith('localhost')) {
    logMessage(
      `Brand resolution failed. No domain match found. Hostname: ${hostname}`
    )
  }

  // Falling back to default brand code when no match is found
  return defaultBrandCode
}

/**
 * Get a a brand from the brandOverride param in the url
 * @param url - The url to get the brand for
 * @returns The brand for the url
 */
export const getBrandOverrideFromUrl = (url: string): Brand | null => {
  const hasBrandOverrideParam =
    url.indexOf('?') !== -1 && url.indexOf('brandOverride') !== -1
  if (!hasBrandOverrideParam) return null

  const search = url.split('?')[1]
  const brandOverride = getUrlParam({
    search,
    param: 'brandOverride'
  })
  return brandOverride ? getBrandByCode(brandOverride as string) : null
}

/**
 * Retrieves the brand code from the URL parameter 'brandOverride' or cookies for brand customization.
 * The override only works in non-production environments.
 *
 * Behavior:
 * - get URL parameter and format: ?brandOverride=brandCode
 * - or get cookie name: 'brandOverride'
 * - if non-production environment, use the override brand code
 * - if override value is invalid, fall back to default brand ('vio')
 * - Persists override in cookie for consistent experience across page reloads
 * @param appEnv - The consumer process environment (e.g., 'development', 'staging', 'production', 'prd')
 * Warning: Don't use process.env.NODE_ENV, use the APP_ENV or equivalent from the env files on each package (accessed through Settings).
 * @returns brandCode - The determined brand code based on override or default.
 */
export const getBrandCodeFromUrlOrCookie = (appEnv?: string) => {
  if (!isBrowser) {
    warnServerEnvironment(`getBrandCodeFromUrlOrCookie`)
    return defaultBrandCode
  }

  const {hostname} = location
  if (!hostname) return defaultBrandCode

  const brandOverride =
    (getUrlParam({
      search: location.search,
      param: 'brandOverride'
    }) as BrandCode) || getBrandOverrideFromCookie()

  const brandOverrideIsValid =
    shouldAllowOverrides(appEnv || '') &&
    Boolean(brandOverride) &&
    Boolean(brands[brandOverride])

  const brandCode = getBrandCodeByHost(hostname, brands)

  if (brandOverrideIsValid) {
    persistBrandOverrideOnCookie(brandOverride)
    return brandOverride
  }

  return brandCode
}

/**
 * Retrieves the URL for the header logo.
 * Some brands can have a custom logo URL set in the brands config.
 * @param brand - The brand object containing brand configuration.
 * @param logoUrl - The custom logo URL to be used if provided.
 * @returns string - The URL for the header logo.
 */
export const getBrandHeaderLogoUrl = (brand: Brand, logoUrl?: string) => {
  return brand.siteAddresses.logoRedirectUrl || logoUrl
}

/**
 * Get subBrand value from URL query string
 * @param search - The search string to get the subBrand from
 * @returns The subBrand value or undefined if not found
 */
const getSubBrandFromUrl = (search: string): string | undefined => {
  const value = getUrlParam({search, param: 'subBrand'}) as string
  return (value && String(value)) || undefined
}

/**
 * Decorates a URL with the subBrand parameter from the current URL if present.
 * Preserves existing query parameters and handles both absolute and relative URLs.
 * @param url - The URL to decorate (can be absolute or relative)
 * @returns The URL with subBrand parameter added if it exists in current URL
 */
export const decorateUrlWithSubBrand = (url: string): string => {
  if (!isBrowser) return url

  const currentSubBrand = getSubBrandFromUrl(location.search)
  if (!currentSubBrand) return url

  try {
    // Handle relative URLs by creating a URL object with current origin
    const urlObj = url.startsWith('/')
      ? new URL(url, window.location.origin)
      : new URL(url)

    // Check if subBrand already exists in the URL
    if (urlObj.searchParams.has('subBrand')) {
      return url
    }

    // Add subBrand parameter
    urlObj.searchParams.set('subBrand', currentSubBrand)

    // Return relative URL if input was relative, otherwise return full URL
    return url.startsWith('/')
      ? urlObj.pathname + urlObj.search + urlObj.hash
      : urlObj.toString()
  } catch (error) {
    // If URL parsing fails, return original URL
    return url
  }
}

/**
 * Resolve subBrand from URL or cookie and persist cookie when URL param is present.
 * Validates against brand.subBrand.brands when enabled.
 * @param brand - The brand object containing brand configuration.
 * @returns The subBrand value or undefined if not found
 */
export const getSubBrandKeyFromUrl = (
  brand: Brand
): SubBrandKey | undefined => {
  if (!isBrowser) return undefined
  if (!brand?.subBrand?.enabled) return undefined

  const urlValue = getSubBrandFromUrl(window.location.search)

  const brandsConfig = brand.subBrand?.brands || {}
  const isValid = (val?: string) => (val ? Boolean(brandsConfig[val]) : false)

  if (isValid(urlValue)) return urlValue
  return undefined
}

/**
 * Gets the appropriate brand code, considering sub-brand configuration.
 * If sub-brand is enabled and a valid sub-brand is found in URL/cookie,
 * returns the sub-brand code. Otherwise, returns the brand's code.
 * @param brand - The brand object containing brand configuration
 * @returns The brand code to use (sub-brand code if available, otherwise brand.code)
 */
export const getBrandCodeWithSubBrand = (
  brand: Brand
): BrandCode | SubBrandCode => {
  if (!brand.subBrand?.enabled) {
    return brand.code
  }

  const subBrandKey = getSubBrandKeyFromUrl(brand)
  if (!subBrandKey) {
    return brand.code
  }

  const subBrandCode = brand.subBrand.brands?.[subBrandKey]?.subBrandCode
  if (subBrandCode) {
    return subBrandCode
  }
  return brand.code
}

/**
 * Resolve the brand logo height for the NavBar logo.
 *
 * Brands may pin the logo to a fixed pixel height via the
 * `shell.mobileLogoFixedHeight` capability (e.g. Kiwi's bulky logo -> 40);
 * otherwise it falls back to the default responsive sizing.
 * @param brand - The brand object containing brand configuration.
 * @param isCompact - Whether the header is compact
 * @returns The logo height
 */
export const getLogoHeight = (brand: Brand, isCompact?: boolean) => {
  const fixedHeight = brand.capabilities.shell.mobileLogoFixedHeight
  if (fixedHeight !== null) {
    return fixedHeight
  }
  return isCompact ? 'auto' : undefined
}

/**
 * Get the subBrand from the server-side context also validates the existence of the subBrand in the brand configuration
 * To be used in getServerSideProps
 * @param brand - The brand object to check for subBrand configuration
 * @param context - Next.js getServerSideProps context
 * @returns The subBrand code if valid, otherwise null
 */
export const getSubBrandFromServerContext = (
  brand: Brand,
  context: GetServerSidePropsContext
): string | undefined => {
  if (!brand.subBrand?.enabled) {
    return undefined
  }

  const subBrandParam = context.query.subBrand as string | undefined
  if (!subBrandParam || !brand.subBrand.brands[subBrandParam]) {
    return undefined
  }

  return subBrandParam
}

/**
 * Get the brand from the server-side context
 * To be used in getServerSideProps
 * @param context - Next.js getServerSideProps context.
 * @returns The determined Brand object.
 */
export const getBrandFromServerContext = (
  context: GetServerSidePropsContext
): Brand => {
  const {req, resolvedUrl} = context

  // On Staging and Production, the host header is set by a lambda function
  // and the host header is not the actual host
  const host = (req.headers['x-host'] as string) || req.headers.host!

  const brandCode = getBrandCodeByHost(host, brands)
  const brand = getBrandByCode(brandCode)

  if (!brand) {
    logMessage(
      `Brand resolution failed. No brand config found for brand code: ${brandCode}. Hostname: ${host}`
    )
  }

  if (!getIsProductionAppEnv(process.env.NEXT_PUBLIC_ENV)) {
    const brandOverride =
      getBrandOverrideFromUrl(resolvedUrl!) ||
      (req.cookies.brandOverride && getBrandByCode(req.cookies.brandOverride))

    if (brandOverride) return brandOverride
  }

  return brand
}

/**
 * Converts old booking management URLs to new format.
 *
 * Transformations:
 * - my.vio.com/my-bookings → vio.com/bookings
 * - my.vio.com/bookings?bookingId=xxx → vio.com/bookings/xxx
 * - my.vio.com/invoice?bookingId=xxx → vio.com/bookings/xxx/invoice
 * - my.vio.com/post-stay-nps?bookingId=xxx → vio.com/bookings/xxx/post-stay-nps
 *
 * Should be removed when there's no active bookings that have received old routes in the emails
 * Optionally ensures lang and currency are present in the resulting URL,
 * preferring explicitly provided values and falling back to values taken from
 * the current browser URL when available. This helps booking-management keep
 * the brand language and currency instead of relying on cookies or brand
 * defaults (which might not exist for non-book-on-brand partners). When
 * language or currency is present, forceLanguageChange=1 and
 * forceCurrencyChange=1 are also set so that booking-management ignores any
 * conflicting cookie values.
 * @param url - The original booking management URL
 * @param brand - The brand object, used to resolve hosts and defaults
 * @param appEnv - The current app environment (e.g. "production", "staging")
 * @param locale - Optional locale (lang) to enforce on the resulting URL
 * @param currency - Optional currency to enforce on the resulting URL
 */
export const convertToNewBookingUrl = (
  url: string,
  brand: Brand,
  appEnv: string,
  locale?: string,
  currency?: string
): string => {
  const currentSearchParams =
    isBrowser && typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : undefined

  const applyLangCurrencyAndForceFlags = (targetUrl: URL) => {
    const hasLang = targetUrl.searchParams.has('lang')
    const hasCurrency = targetUrl.searchParams.has('currency')

    if (!hasLang) {
      const effectiveLang =
        locale || currentSearchParams?.get('lang') || undefined
      if (effectiveLang) {
        targetUrl.searchParams.set('lang', effectiveLang)
      }
    }

    if (!hasCurrency) {
      const effectiveCurrency =
        currency || currentSearchParams?.get('currency') || undefined
      if (effectiveCurrency) {
        targetUrl.searchParams.set('currency', effectiveCurrency)
      }
    }

    if (targetUrl.searchParams.has('lang')) {
      targetUrl.searchParams.set('forceLanguageChange', '1')
    }

    if (targetUrl.searchParams.has('currency')) {
      targetUrl.searchParams.set('forceCurrencyChange', '1')
    }
  }

  try {
    const oldUrl = new URL(url)
    const newHost = getSearchHost({brand, appEnv}) ?? url
    const newUrl = new URL(newHost)

    // URL is already on the new domain — no path transformation needed,
    // but we still want to ensure lang/currency and force flags are applied.
    if (oldUrl.hostname === newUrl.hostname) {
      applyLangCurrencyAndForceFlags(oldUrl)
      return oldUrl.toString()
    }

    const {pathname: oldPathname} = oldUrl
    const bookingId = oldUrl.searchParams.get('bookingId')

    if (oldPathname === '/my-bookings') {
      newUrl.pathname = '/bookings'
    } else if (/^\/bookings\/?$/.test(oldPathname)) {
      if (bookingId) {
        newUrl.pathname = `/bookings/${bookingId}`
        oldUrl.searchParams.delete('bookingId')
      } else {
        newUrl.pathname = '/bookings'
      }
    } else if (oldPathname === '/invoice' && bookingId) {
      newUrl.pathname = `/bookings/${bookingId}/invoice`
      oldUrl.searchParams.delete('bookingId')
    } else if (oldPathname === '/post-stay-nps' && bookingId) {
      newUrl.pathname = `/bookings/${bookingId}/post-stay-nps`
      oldUrl.searchParams.delete('bookingId')
    } else {
      newUrl.pathname = oldPathname
    }

    // Copy remaining query params from old URL to new URL
    oldUrl.searchParams.forEach((value, key) => {
      newUrl.searchParams.set(key, value)
    })

    applyLangCurrencyAndForceFlags(newUrl)

    return newUrl.toString()
  } catch {
    const host = getSearchHost({brand, appEnv})
    try {
      const fallbackUrl = new URL(`${host}/bookings`)
      applyLangCurrencyAndForceFlags(fallbackUrl)
      return fallbackUrl.toString()
    } catch {
      return `${host}/bookings`
    }
  }
}
